// Audit event log for the Sunstone platform.
//
// This package defines the audit trail for administrative actions performed
// via the Management Plane. Every mutation that affects tenants, users,
// deployments, or workspaces produces an AuditEvent that records who did
// what, on which resource, and when.
//
// Audit events are immutable once written. The service exposes read-only
// RPCs for retrieval and listing with optional filtering by resource or actor.
//
// See also:
//   - tenants/v1/tenants.proto  — tenant and user management
//   - deployments/v1/deployments.proto — cloud deployments and workspaces
//
// Docs: https://connectrpc.com/docs/introduction

// @generated by protoc-gen-es v2.11.0 with parameter "target=ts"
// @generated from file audit/v1/audit.proto (package audit.v1, syntax proto3)
/* eslint-disable */

import type { GenFile, GenMessage, GenService } from "@bufbuild/protobuf/codegenv2";
import { fileDesc, messageDesc, serviceDesc } from "@bufbuild/protobuf/codegenv2";
import type { Timestamp } from "@bufbuild/protobuf/wkt";
import { file_google_protobuf_struct, file_google_protobuf_timestamp } from "@bufbuild/protobuf/wkt";
import type { JsonObject, Message } from "@bufbuild/protobuf";

/**
 * Describes the file audit/v1/audit.proto.
 */
export const file_audit_v1_audit: GenFile = /*@__PURE__*/
  fileDesc("ChRhdWRpdC92MS9hdWRpdC5wcm90bxIIYXVkaXQudjEihAIKCkF1ZGl0RXZlbnQSCgoCaWQYASABKAkSEgoKZXZlbnRfdHlwZRgCIAEoCRIWCg5zY2hlbWFfdmVyc2lvbhgDIAEoBRIVCg1yZXNvdXJjZV9raW5kGAQgASgJEhMKC3Jlc291cmNlX2lkGAUgASgJEhAKCGFjdG9yX2lkGAYgASgJEhIKCmFjdG9yX2tpbmQYByABKAkSEwoLZGVzY3JpcHRpb24YCCABKAkSJwoGcGFyYW1zGAkgASgLMhcuZ29vZ2xlLnByb3RvYnVmLlN0cnVjdBIuCgpjcmVhdGVkX2F0GBQgASgLMhouZ29vZ2xlLnByb3RvYnVmLlRpbWVzdGFtcCK7AQoWTGlzdEF1ZGl0RXZlbnRzUmVxdWVzdBIaCg1yZXNvdXJjZV9raW5kGAEgASgJSACIAQESGAoLcmVzb3VyY2VfaWQYAiABKAlIAYgBARIVCghhY3Rvcl9pZBgDIAEoCUgCiAEBEhEKCXBhZ2Vfc2l6ZRgKIAEoBRISCgpwYWdlX3Rva2VuGAsgASgJQhAKDl9yZXNvdXJjZV9raW5kQg4KDF9yZXNvdXJjZV9pZEILCglfYWN0b3JfaWQiWAoXTGlzdEF1ZGl0RXZlbnRzUmVzcG9uc2USJAoGZXZlbnRzGAEgAygLMhQuYXVkaXQudjEuQXVkaXRFdmVudBIXCg9uZXh0X3BhZ2VfdG9rZW4YAiABKAkiIgoUR2V0QXVkaXRFdmVudFJlcXVlc3QSCgoCaWQYASABKAkiPAoVR2V0QXVkaXRFdmVudFJlc3BvbnNlEiMKBWV2ZW50GAEgASgLMhQuYXVkaXQudjEuQXVkaXRFdmVudDLCAQoMQXVkaXRTZXJ2aWNlElsKD0xpc3RBdWRpdEV2ZW50cxIgLmF1ZGl0LnYxLkxpc3RBdWRpdEV2ZW50c1JlcXVlc3QaIS5hdWRpdC52MS5MaXN0QXVkaXRFdmVudHNSZXNwb25zZSIDkAIBElUKDUdldEF1ZGl0RXZlbnQSHi5hdWRpdC52MS5HZXRBdWRpdEV2ZW50UmVxdWVzdBofLmF1ZGl0LnYxLkdldEF1ZGl0RXZlbnRSZXNwb25zZSIDkAIBQj1aO2dpdGh1Yi5jb20vdW5pcGhvcmUvc3Vuc3RvbmUvcGtnL2dvL2dlbi9wYi9hdWRpdC92MTthdWRpdFYxYgZwcm90bzM", [file_google_protobuf_timestamp, file_google_protobuf_struct]);

/**
 * AuditEvent represents a single immutable record of an administrative action.
 *
 * Events capture the full context of a mutation: what happened (event_type),
 * which resource was affected (resource_kind + resource_id), who performed the
 * action (actor_id + actor_kind), a human-readable description, and arbitrary
 * structured parameters for event-type-specific detail.
 *
 * @generated from message audit.v1.AuditEvent
 */
export type AuditEvent = Message<"audit.v1.AuditEvent"> & {
  /**
   * Unique audit event identifier.
   * Format: audit_[20 lowercase alphanumeric characters]
   *
   * @generated from field: string id = 1;
   */
  id: string;

  /**
   * Machine-readable event type (e.g. "tenant.created", "user.deactivated").
   *
   * @generated from field: string event_type = 2;
   */
  eventType: string;

  /**
   * Schema version for the params payload. Incremented when the shape of
   * params changes in a backwards-incompatible way.
   *
   * @generated from field: int32 schema_version = 3;
   */
  schemaVersion: number;

  /**
   * Kind of resource that was affected (e.g. "tenant", "user", "deployment").
   *
   * @generated from field: string resource_kind = 4;
   */
  resourceKind: string;

  /**
   * ID of the resource that was affected.
   *
   * @generated from field: string resource_id = 5;
   */
  resourceId: string;

  /**
   * ID of the actor who performed the action.
   *
   * @generated from field: string actor_id = 6;
   */
  actorId: string;

  /**
   * Kind of actor (e.g. "user", "service").
   *
   * @generated from field: string actor_kind = 7;
   */
  actorKind: string;

  /**
   * Human-readable description of the event suitable for display in audit logs.
   *
   * @generated from field: string description = 8;
   */
  description: string;

  /**
   * Structured event-specific parameters. Shape is versioned by schema_version.
   *
   * @generated from field: google.protobuf.Struct params = 9;
   */
  params?: JsonObject;

  /**
   * When the event was recorded.
   *
   * @generated from field: google.protobuf.Timestamp created_at = 20;
   */
  createdAt?: Timestamp;
};

/**
 * Describes the message audit.v1.AuditEvent.
 * Use `create(AuditEventSchema)` to create a new message.
 */
export const AuditEventSchema: GenMessage<AuditEvent> = /*@__PURE__*/
  messageDesc(file_audit_v1_audit, 0);

/**
 * ListAuditEventsRequest returns audit events with optional filtering.
 * Results are ordered by creation time descending (newest first).
 *
 * @generated from message audit.v1.ListAuditEventsRequest
 */
export type ListAuditEventsRequest = Message<"audit.v1.ListAuditEventsRequest"> & {
  /**
   * Filter to events for a specific resource kind (e.g. "tenant").
   *
   * @generated from field: optional string resource_kind = 1;
   */
  resourceKind?: string;

  /**
   * Filter to events for a specific resource ID.
   *
   * @generated from field: optional string resource_id = 2;
   */
  resourceId?: string;

  /**
   * Filter to events performed by a specific actor ID.
   *
   * @generated from field: optional string actor_id = 3;
   */
  actorId?: string;

  /**
   * Maximum number of results to return. Server may return fewer.
   *
   * @generated from field: int32 page_size = 10;
   */
  pageSize: number;

  /**
   * Pagination token from a previous ListAuditEventsResponse.
   *
   * @generated from field: string page_token = 11;
   */
  pageToken: string;
};

/**
 * Describes the message audit.v1.ListAuditEventsRequest.
 * Use `create(ListAuditEventsRequestSchema)` to create a new message.
 */
export const ListAuditEventsRequestSchema: GenMessage<ListAuditEventsRequest> = /*@__PURE__*/
  messageDesc(file_audit_v1_audit, 1);

/**
 * @generated from message audit.v1.ListAuditEventsResponse
 */
export type ListAuditEventsResponse = Message<"audit.v1.ListAuditEventsResponse"> & {
  /**
   * @generated from field: repeated audit.v1.AuditEvent events = 1;
   */
  events: AuditEvent[];

  /**
   * Token for the next page of results. Empty if no more results.
   *
   * @generated from field: string next_page_token = 2;
   */
  nextPageToken: string;
};

/**
 * Describes the message audit.v1.ListAuditEventsResponse.
 * Use `create(ListAuditEventsResponseSchema)` to create a new message.
 */
export const ListAuditEventsResponseSchema: GenMessage<ListAuditEventsResponse> = /*@__PURE__*/
  messageDesc(file_audit_v1_audit, 2);

/**
 * @generated from message audit.v1.GetAuditEventRequest
 */
export type GetAuditEventRequest = Message<"audit.v1.GetAuditEventRequest"> & {
  /**
   * The audit event ID to retrieve.
   * Format: audit_[20 lowercase alphanumeric characters]
   *
   * @generated from field: string id = 1;
   */
  id: string;
};

/**
 * Describes the message audit.v1.GetAuditEventRequest.
 * Use `create(GetAuditEventRequestSchema)` to create a new message.
 */
export const GetAuditEventRequestSchema: GenMessage<GetAuditEventRequest> = /*@__PURE__*/
  messageDesc(file_audit_v1_audit, 3);

/**
 * @generated from message audit.v1.GetAuditEventResponse
 */
export type GetAuditEventResponse = Message<"audit.v1.GetAuditEventResponse"> & {
  /**
   * @generated from field: audit.v1.AuditEvent event = 1;
   */
  event?: AuditEvent;
};

/**
 * Describes the message audit.v1.GetAuditEventResponse.
 * Use `create(GetAuditEventResponseSchema)` to create a new message.
 */
export const GetAuditEventResponseSchema: GenMessage<GetAuditEventResponse> = /*@__PURE__*/
  messageDesc(file_audit_v1_audit, 4);

/**
 * AuditService provides read-only access to the platform audit log.
 *
 * Audit events are written internally by the Management Plane as a side-effect
 * of mutations. This service exposes retrieval and listing with optional
 * filtering by resource or actor. All RPCs are read-only (NO_SIDE_EFFECTS).
 *
 * @generated from service audit.v1.AuditService
 */
export const AuditService: GenService<{
  /**
   * ListAuditEvents returns audit events ordered by creation time descending.
   * Results may be filtered by resource_kind, resource_id, or actor_id.
   *
   * @generated from rpc audit.v1.AuditService.ListAuditEvents
   */
  listAuditEvents: {
    methodKind: "unary";
    input: typeof ListAuditEventsRequestSchema;
    output: typeof ListAuditEventsResponseSchema;
  },
  /**
   * GetAuditEvent retrieves a single audit event by ID.
   *
   * @generated from rpc audit.v1.AuditService.GetAuditEvent
   */
  getAuditEvent: {
    methodKind: "unary";
    input: typeof GetAuditEventRequestSchema;
    output: typeof GetAuditEventResponseSchema;
  },
}> = /*@__PURE__*/
  serviceDesc(file_audit_v1_audit, 0);

